import React from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function Counter({ label, value, onChange, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-counter`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-slate-700" data-testid={`${testIdPrefix}-label`}>{label}</div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <Button
          data-testid={`${testIdPrefix}-decrement`}
          type="button"
          variant="secondary"
          className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
          onClick={() => onChange(Math.max(0, value - 1))}
        >
          -
        </Button>
        <div className="text-2xl font-semibold" data-testid={`${testIdPrefix}-value`}>{value}</div>
        <Button
          data-testid={`${testIdPrefix}-increment`}
          type="button"
          className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
          onClick={() => onChange(value + 1)}
        >
          +
        </Button>
      </div>
    </div>
  );
}

export function CaptureDetailsPanel({
  activityType,
  setActivityType,
  cases,
  setCases,
  sacks,
  setSacks,
  notes,
  setNotes,
  placeName,
  setPlaceName,
  clipMinutes,
  setClipMinutes,
}) {
  return (
    <div className="space-y-4" data-testid="capture-details-panel">
      {/* 1. Activity */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-sm text-slate-700 font-medium" data-testid="activity-title">Activity</div>
        <Tabs value={activityType} onValueChange={setActivityType} data-testid="activity-type-tabs">
          <TabsList
            className="mt-3 grid w-full grid-cols-2 rounded-2xl bg-slate-100 border border-slate-200 p-1"
            data-testid="activity-type-tabs-list"
          >
            <TabsTrigger
              value="loading"
              data-testid="activity-type-loading"
              className="rounded-xl text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
            >
              Loading
            </TabsTrigger>
            <TabsTrigger
              value="unloading"
              data-testid="activity-type-unloading"
              className="rounded-xl text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
            >
              Unloading
            </TabsTrigger>
          </TabsList>
          <TabsContent value="loading" data-testid="activity-type-loading-content">
            <div className="mt-2 text-xs text-slate-500">Use this for dispatch/loading proof.</div>
          </TabsContent>
          <TabsContent value="unloading" data-testid="activity-type-unloading-content">
            <div className="mt-2 text-xs text-slate-500">Use this for receipt/unloading proof.</div>
          </TabsContent>
        </Tabs>
      </div>

      {/* 2. Place & Notes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
        <div className="text-sm text-slate-700 font-medium" data-testid="location-notes-title">Location & Notes</div>
        <div className="space-y-2">
          <Label data-testid="place-name-label" htmlFor="place">Place name</Label>
          <Input
            id="place"
            data-testid="place-name-input"
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            placeholder="E.g. Delhi Warehouse"
            className="pod-input-light bg-white border-slate-300"
          />
          <div className="text-xs text-slate-500" data-testid="place-name-hint">
            Used in filename + watermark.
          </div>
        </div>

        <div className="space-y-2">
          <Label data-testid="notes-label" htmlFor="notes">Notes (optional)</Label>
          <Input
            id="notes"
            data-testid="notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Vehicle, route, consignee…"
            className="pod-input-light bg-white border-slate-300"
          />
        </div>
      </div>

      {/* 3. Auto Clip */}
      {clipMinutes !== undefined && setClipMinutes && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm" data-testid="drawer-clip-settings">
          <div className="text-sm text-slate-900 font-medium" data-testid="drawer-clip-title">Auto clip</div>
          <div className="mt-3 space-y-2">
            <Label data-testid="drawer-clip-minutes-label" htmlFor="drawerClipMinutes">Auto-stop every (minutes)</Label>
            <Input
              id="drawerClipMinutes"
              data-testid="drawer-clip-minutes-input"
              type="number"
              min={1}
              max={60}
              value={clipMinutes}
              onChange={(e) => setClipMinutes(e.target.value)}
              className="pod-input-light bg-white border-slate-300"
            />
            <div className="text-xs text-slate-600" data-testid="drawer-clip-minutes-help">
              Default 5. Recommended 4–8 for weaker phones.
            </div>
          </div>
        </div>
      )}

      {/* 4. Counters */}
      <div>
        <div className="text-sm text-slate-700 font-medium mb-3" data-testid="counters-title">Counters</div>
        <div className="grid grid-cols-2 gap-3" data-testid="counters-grid">
          <Counter label="Cases" value={cases} onChange={setCases} testIdPrefix="cases" />
          <Counter label="Sacks" value={sacks} onChange={setSacks} testIdPrefix="sacks" />
        </div>
      </div>
    </div>
  );
}

export default function CaptureDetailsDrawer(props) {
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button
          data-testid="details-drawer-open"
          type="button"
          variant="secondary"
          className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
        >
          Details
        </Button>
      </DrawerTrigger>
      <DrawerContent className="border-slate-200 bg-white text-slate-900" data-testid="details-drawer-content">
        <DrawerHeader>
          <DrawerTitle data-testid="details-drawer-title">Capture details</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-6" data-testid="details-drawer-body">
          <CaptureDetailsPanel {...props} />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
